import { gql, useMutation } from "@apollo/client";
import {
  CalendarMonth,
  CalendarToday,
  Redo,
  Repeat,
  RestartAlt,
  Schedule,
} from "@mui/icons-material";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grid,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  addDays,
  addMonths,
  differenceInDays,
  differenceInMonths,
  eachDayOfInterval,
  format,
  getWeeksInMonth,
  isSameDay,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import HourFormat from "../../util/hourFormat";
import { DELETE_OVERRIDE_GROUP } from "../../graphql/mutations";

const AvailabilityCalendar = (props) => {
  // Define various states used in the component
  const [selectedDates, setSelectedDates] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteOverrideGroup] = useMutation(DELETE_OVERRIDE_GROUP);
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [use, setUse] = useState([]);
  const [selectedDate, setSelectedDate] = useState();
  const calendarRef = useRef(null);

  // This function returns all dates of the month including the extra days to make a complete week in the calendar
  const getMonthDates = (date) => {
    if (!date) {
      return false;
    }
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const firstDayOfCalendar = new Date(
      firstDayOfMonth.getFullYear(),
      firstDayOfMonth.getMonth(),
      firstDayOfMonth.getDate() - firstDayOfMonth.getDay()
    );
    const lastDayOfCalendar = new Date(
      lastDayOfMonth.getFullYear(),
      lastDayOfMonth.getMonth(),
      lastDayOfMonth.getDate() + (6 - lastDayOfMonth.getDay())
    );

    let dates = [];
    let currentDate = firstDayOfCalendar;
    while (currentDate <= lastDayOfCalendar) {
      dates.push(currentDate);
      currentDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 1
      );
    }
    for (let i = 0; i < 7; i++) {
      dates.push(currentDate);
      currentDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 1
      );
    }
    return dates;
  };
  // This function handles the mouse move event when dragging on the calendar to select multiple dates
  function handleMouseMove(event) {
    if (dragStart) {
      const calendarRect = calendarRef.current.getBoundingClientRect();
      const calendarWidth = calendarRect.width;
      const calendarHeight = calendarRect.height;
      const dayWidth = calendarWidth / 7;
      const dayHeight = calendarHeight / getWeeksInMonth(currentMonth) + 1 - 23;
      const offsetX = event.clientX - calendarRect.left;
      const offsetY = event.clientY - calendarRect.top;
      const colIndex = Math.floor(offsetX / dayWidth);
      const rowIndex = Math.floor(offsetY / dayHeight);
      const cellDate = addDays(
        startOfWeek(startOfMonth(currentMonth)),
        rowIndex * 7 + colIndex
      );
      cellDate &&
        selectedDate &&
        setSelectedDates(
          eachDayOfInterval(
            selectedDate > cellDate
              ? {
                  start: cellDate,
                  end: selectedDate,
                }
              : { start: selectedDate, end: cellDate }
          ).filter((val) => val >= new Date() || isSameDay(val, new Date()))
        );
    }
  }

  // This function handles the mouse down event when a date is clicked in the calendar
  const handleMouseDown = (event, date) => {
    if (date >= new Date() || isSameDay(date, new Date())) {
      setDragStart(date);
      setSelectedDate(date);
      if (selectedDates.length) {
        setSelectedDates([date]);
      }
    }
    setOpen(false);
  };

  // This function handles the mouse up event when dragging is finished on the calendar
  const handleMouseUp = (event) => {
    setDragStart(null);
    setDragEnd(null);
    setUse(selectedDates);
    if (selectedDates.length > 1) {
      if (
        !selectedDates.some((date) =>
          data.some((override) =>
            isSameDay(new Date(override.date), new Date(date))
          )
        )
      ) {
        props.setJobs([
          {
            startTime: HourFormat() == "HH:mm" ? "9:00" : "9:00am",
            endTime: HourFormat() == "HH:mm" ? "17:00" : "5:00pm",
          },
        ]);
        props.setSelectedDates(selectedDates);
        setSelectedDates([]);
      } else {
        setOpen(true);
      }
    }
  };

  // Convert the overrideGroup prop into a better format to work with
  var data = props.overrideGroup.map((val) => {
    return {
      ...val,
      date: new Date(new Date(val.date)),
    };
  });

  // Get all dates to display on the calendar for the current month
  const getMonths = getMonthDates(currentMonth);

  // Tooltip state and handlers
  const [tooltip, setTooltipOpen] = useState(getMonths.map((val) => false));
  const handleTooltipOpen = (i) => {
    var e = tooltip.slice();
    e[i] = true;
    setTooltipOpen(e);
  };

  const handleTooltipClose = (i) => {
    var e = tooltip.slice();
    e[i] = false;
    setTooltipOpen(e);
  };
  const [targets, setTargets] = useState({});

  // Handle the click event for a date in the calendar
  const handleClick = (event, infos) => {
    event.stopPropagation();
    setUse([new Date(infos?.date || infos)]);
    setAnchorEl(event.currentTarget);
    setOpen(
      isSameDay(new Date(info?.date || info), new Date(infos?.date || infos)) &&
        open
        ? false
        : !open
        ? !open
        : open
    );
    setSelectedDates([infos.date || infos]);
    setSelectedDate(new Date(infos?.date || infos));
    setInfo(new Date(infos?.date || infos));
  };

  // Define the Hover component for hovered dates in the calendar
  const Hover = (propss) => {
    return (
      <div
        className="hovered"
        onClick={() => {
          propss.onClick();
          setOpen(false);
          setSelectedDates([]);
        }}
        style={{
          display: propss.leg || selectedDates ? "flex" : "none",
        }}
      >
        {propss.children}
      </div>
    );
  };

  // This function deletes an override group
  const deleteOverride = () => {
    selectedDates.map((val) =>
      data?.find((vall) => isSameDay(new Date(vall.date), new Date(val)))?.id
        ? deleteOverrideGroup({
            variables: {
              id: parseInt(
                data?.find((vall) =>
                  isSameDay(new Date(vall.date), new Date(val))
                )?.id
              ),
            },
          })
            .then((res) => {
              props.overrideGroupRefetch();
              props.overrideRefetch();
              props.setUpdating(true);
            })
            .catch((err) => {
              props.overrideRefetch();
              console.error(err);
            })
        : () => {}
    );
  };
  return (
    <>
      <div
        style={{
          height: "100px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div>
          <Popper
            open={open}
            anchorEl={anchorEl}
            style={{ zIndex: 5 }}
            placement="bottom-start"
          >
            <Paper
              style={{ width: "200px", padding: "10px 0" }}
              sx={{
                boxShadow: 3,
              }}
            >
              <Hover
                leg
                onClick={() => {
                  // This onClick event handler function sets jobs based on the selected dates.
                  // If multiple dates are selected, default startTime and endTime are used.
                  // If a single date is selected, it checks if that date is in data. If it is,
                  // it filters override data by matching groupId with the id from data.
                  // If the date is not in data, it filters jobs by matching day of week and onJob status.
                  let newJobs;

                  if (selectedDates?.length > 1) {
                    newJobs = [
                      {
                        startTime: HourFormat() === "HH:mm" ? "9:00" : "9:00am",
                        endTime: HourFormat() === "HH:mm" ? "17:00" : "5:00pm",
                      },
                    ];
                  } else {
                    const selectedDateData = data?.find((vall) =>
                      isSameDay(new Date(vall.date), new Date(selectedDates[0]))
                    );

                    newJobs = selectedDateData
                      ? props.override
                          .filter(
                            (valll) => valll.groupId == selectedDateData?.id
                          )
                          .map((valll) => ({
                            startTime: valll.startTime,
                            endTime: valll.endTime,
                          }))
                      : props.data
                          .filter(
                            (vall) =>
                              vall.day ===
                                format(new Date(selectedDates[0]), "EEEE") &&
                              vall.onJob === true
                          )
                          .map((valll) => ({
                            startTime: valll.startTime,
                            endTime: valll.endTime,
                          }));
                  }

                  props.setJobs(newJobs);
                  props.setSelectedDates(use);
                  setSelectedDate();
                  setSelectedDates([]);
                }}
              >
                <CalendarToday style={{ fontSize: "20px" }} />{" "}
                <Typography variant="contained" style={{ color: "black" }}>
                  Edit date{selectedDates.length > 1 && "s"}
                </Typography>
              </Hover>
              {/* The logic in this block checks if a single date is selected and if that date is in data.
              If these conditions are met, it displays an option to "Edit all day's jobs".
              If the conditions are not met, it displays an option to "Reset to weekly hours". */}
              {selectedDates?.length === 1 &&
              !data?.find((vall) =>
                isSameDay(new Date(vall.date), new Date(selectedDates[0]))
              ) ? (
                <Hover
                  onClick={() => {
                    props.modal(selectedDates[0]);
                    setSelectedDate();
                    setSelectedDates([]);
                  }}
                >
                  <Redo style={{ fontSize: "20px" }} />{" "}
                  <Typography variant="contained" style={{ color: "black" }}>
                    Edit all {format(selectedDates[0], "EEEE")}s
                  </Typography>
                </Hover>
              ) : (
                <Hover
                  onClick={() => {
                    deleteOverride();
                  }}
                >
                  <RestartAlt style={{ fontSize: "20px" }} />{" "}
                  <Typography variant="contained" style={{ color: "black" }}>
                    Reset to weekly hours
                  </Typography>
                </Hover>
              )}
            </Paper>
          </Popper>
        </div>
        {/* This block of code displays the current month and allows you to set your weekly hours */}
        <div
          style={{
            flex: 1,
            padding: "20px",
            gap: "5px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <h2 style={{ margin: 0 }}>{format(currentMonth, "MMMM YYY")}</h2>
          <Typography>Set your weekly hours</Typography>
        </div>

        {/* This block of code is responsible for the navigation buttons, moving to today's date, and switching between months */}
        <div
          style={{
            flex: 1,
            justifyContent: "flex-end",
            padding: "20px",
            display: "flex",
            gap: "20px",
          }}
        >
          {Math.abs(differenceInMonths(currentMonth, new Date())) > 12 && (
            <Button
              variant="outlined"
              onClick={() => setCurrentMonth(new Date())}
            >
              Today
            </Button>
          )}
          <ButtonGroup>
            <Button
              onClick={() => {
                setOpen(false);
                setCurrentMonth(subMonths(currentMonth, 1));
              }}
            >
              {"<"}
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                setCurrentMonth(addMonths(currentMonth, 1));
              }}
            >
              {">"}
            </Button>
          </ButtonGroup>
        </div>
      </div>
      {/* This block of code creates the days of the week header for the calendar */}
      <div
        style={{
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          width: "100%",
          display: "flex",
        }}
      >
        {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((val, ind) => (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              fontWeight: 700,
            }}
          >
            {val}
          </div>
        ))}
      </div>
      <Grid
        container
        ref={calendarRef}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {getMonths.map((val, ind) => {
          var verify = differenceInDays(new Date(), val) > 0;
          return (
            <Grid
              item
              onMouseOver={(event) => {
                !open && setAnchorEl(event.currentTarget);
              }}
              onClick={
                !verify
                  ? (event) =>
                      handleClick(
                        event,
                        data?.find((vall) =>
                          isSameDay(new Date(vall.date), new Date(val))
                        ) || val
                      )
                  : () => {
                      setInfo();
                      setSelectedDate();
                      setSelectedDates([]);
                    }
              }
              onMouseMove={handleMouseMove}
              onMouseDown={(event) => handleMouseDown(event, val)}
              xs={1.71428571}
              className={!verify ? "card" : ""}
              style={
                selectedDates?.find((vall) =>
                  isSameDay(new Date(vall), new Date(val))
                )
                  ? {
                      userSelect: "none",
                      padding: "10px",
                      width: "140px",
                      height: "140px",
                      border:
                        "1px solid var(--text-color-level3,rgba(26,26,26,0.1))",
                      background: "rgba(0, 106, 255, 0.1)",
                      borderTop: data?.find((vall) =>
                        isSameDay(new Date(vall.date), new Date(val))
                      )
                        ? props.override.find(
                            (valll) =>
                              valll.groupId ==
                              data?.find((vall) =>
                                isSameDay(new Date(vall.date), new Date(val))
                              )?.id
                          )
                          ? "3px solid #5a91e2"
                          : "4px solid grey"
                        : ind < 7 && "none",
                      borderLeft: ind < 7 && "none",
                    }
                  : {
                      userSelect: "none",
                      padding: "10px",
                      width: "140px",
                      height: "140px",
                      border:
                        "1px solid var(--text-color-level3,rgba(26,26,26,0.1))",
                      borderTop: data?.find((vall) =>
                        isSameDay(new Date(vall.date), new Date(val))
                      )
                        ? props.override.find(
                            (valll) =>
                              valll.groupId ==
                              data?.find((vall) =>
                                isSameDay(new Date(vall.date), new Date(val))
                              )?.id
                          )
                          ? "3px solid #5a91e2"
                          : "4px solid grey"
                        : ind < 7 && "none",
                      borderLeft: ind < 7 && "none",
                      background: verify && "rgb(245,245,245)",
                    }
              }
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    background: isToday(val) && "rgba(26,26,26,0.1)",
                    width: "30px",
                    height: "30px",
                    borderRadius: isToday(val) && "100%",
                    display: isToday(val) && "flex",
                    alignItems: isToday(val) && "center",
                    justifyContent: isToday(val) && "center",
                  }}
                >
                  {val.getDate()}{" "}
                </Typography>
                {data?.find((vall) =>
                  isSameDay(new Date(vall.date), new Date(val))
                ) ? (
                  <Tooltip
                    title="Your availability for this specific date."
                    placement="right"
                    open={!!tooltip[ind]}
                    onClose={() => handleTooltipClose(ind)}
                    onOpen={() => handleTooltipOpen(ind)}
                    arrow
                    style={{ width: "10px", height: "10px" }}
                    classes={{
                      tooltip: "tooltip",
                      arrow: "tooltip-arrow",
                    }}
                  >
                    <CalendarMonth
                      onClick={handleTooltipOpen}
                      onMouseEnter={() => handleTooltipOpen(ind)}
                      onMouseLeave={() => handleTooltipClose(ind)}
                      style={{
                        opacity: "90%",
                        color: props.override.find(
                          (valll) =>
                            valll.groupId ==
                            data?.find((vall) =>
                              isSameDay(new Date(vall.date), new Date(val))
                            )?.id
                        )
                          ? "#5a91e2"
                          : "grey",
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip
                    title="Your availability for this day repeats weekly."
                    placement="right"
                    open={tooltip[ind]}
                    onClose={() => handleTooltipClose(ind)}
                    onOpen={() => handleTooltipOpen(ind)}
                    arrow
                    style={{ width: "10px", height: "10px" }}
                    classes={{
                      tooltip: "tooltip",
                      arrow: "tooltip-arrow",
                    }}
                  >
                    <Repeat
                      onMouseEnter={() => handleTooltipOpen(ind)}
                      onMouseLeave={() => handleTooltipClose(ind)}
                      style={{ opacity: "40%" }}
                    />
                  </Tooltip>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  paddingTop: "10px",
                  height: "45%",
                }}
              >
                {(data?.find((vall) =>
                  isSameDay(new Date(vall.date), new Date(val))
                )
                  ? props.override.filter(
                      (valll) =>
                        valll.groupId ==
                        data?.find((vall) =>
                          isSameDay(new Date(vall.date), new Date(val))
                        )?.id
                    ).length
                  : props.data.filter(
                      (vall) =>
                        vall.day == format(val, "EEEE") && vall.onJob == true
                    ).length) == 0 ? (
                  <div
                    style={{ height: "100%", width: "100%", opacity: "50%" }}
                    className="flex align justify"
                  >
                    <Typography>Unavailable</Typography>
                  </div>
                ) : (
                  ""
                )}
                {data?.find((vall) =>
                  isSameDay(new Date(vall.date), new Date(val))
                )
                  ? props.override
                      .filter(
                        (valll) =>
                          valll.groupId ==
                          data?.find((vall) =>
                            isSameDay(new Date(vall.date), new Date(val))
                          )?.id
                      )
                      .map((valll, ind) =>
                        ind == 3 ? (
                          <h5 style={{ fontWeight: 300, margin: 0 }}>
                            +
                            {props.override.filter(
                              (valll) =>
                                valll.groupId ==
                                data?.find((vall) =>
                                  isSameDay(new Date(vall.date), new Date(val))
                                )?.id
                            ).length - 3}{" "}
                            more
                          </h5>
                        ) : ind < 3 ? (
                          <h5 style={{ fontWeight: 300, margin: 0 }}>
                            {valll?.startTime + " - " + valll?.endTime}
                          </h5>
                        ) : (
                          ""
                        )
                      )
                  : props.data
                      .filter(
                        (vall) =>
                          vall.day == format(val, "EEEE") && vall.onJob == true
                      )
                      .map((valll, ind) =>
                        ind == 3 ? (
                          <h5 style={{ fontWeight: 300, margin: 0 }}>
                            +
                            {props.data.filter(
                              (vall) => vall.day == format(val, "EEEE")
                            ).length - 3}{" "}
                            more
                          </h5>
                        ) : ind < 3 ? (
                          <h5 style={{ fontWeight: 300, margin: 0 }}>
                            {valll?.startTime + " - " + valll?.endTime}
                          </h5>
                        ) : (
                          ""
                        )
                      )}
              </div>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};
export default AvailabilityCalendar;
